import computePortfolioPnlValues from 'src/components/PortfolioPage/PortfolioTradeSubPage/utils/computePortfolioPnlValues';
import {
	BuildPortfolioChartBaseDataParams,
	BuildPortfolioChartLiveDataParams,
} from 'src/components/PortfolioPage/PortfolioTradeSubPage/types';
import computePortfolioTradeDelta from 'src/components/PortfolioPage/utils/computePortfolioTradeDelta';

export function buildCumulativePnlBaseData({
	enabled,
	snapshots,
	includeFeesAndFunding,
	realizedPnlOnly,
}: BuildPortfolioChartBaseDataParams) {
	if (!enabled || !snapshots?.length) {
		return {
			series: [],
			startOfPeriodTotalPnl: 0,
			startOfPeriodFeesAndFunding: 0,
		};
	}

	const startOfPeriodSnapshot = snapshots[0];

	const {
		totalPnl: startOfPeriodTotalPnl,
		feesAndFunding: startOfPeriodFeesAndFunding,
	} = computePortfolioPnlValues({
		cumulativeRealizedPnl: startOfPeriodSnapshot.cumulativeRealizedPnl,
		unrealizedPnl: undefined,
		cumulativeFunding: startOfPeriodSnapshot.cumulativeFunding,
		cumulativeFeePaid: startOfPeriodSnapshot.cumulativeFeePaid,
		cumulativeFeeRebate: startOfPeriodSnapshot.cumulativeFeeRebate,
		includeFeesAndFunding,
		realizedPnlOnly,
	});

	const series = snapshots.map((snapshot) => {
		const {
			totalPnl: currentSnapshotTotalPnl,
			feesAndFunding: currentSnapshotFeesAndFunding,
		} = computePortfolioPnlValues({
			cumulativeRealizedPnl: snapshot.cumulativeRealizedPnl,
			unrealizedPnl: snapshot.unrealizedPnl,
			cumulativeFunding: snapshot.cumulativeFunding,
			cumulativeFeePaid: snapshot.cumulativeFeePaid,
			cumulativeFeeRebate: snapshot.cumulativeFeeRebate,
			includeFeesAndFunding,
			realizedPnlOnly,
		});

		const cumulativeTotalPnl = computePortfolioTradeDelta(
			currentSnapshotTotalPnl,
			startOfPeriodTotalPnl
		);
		const cumulativeFeesAndFunding = computePortfolioTradeDelta(
			currentSnapshotFeesAndFunding,
			startOfPeriodFeesAndFunding
		);

		return {
			date: new Date(snapshot.ts * 1000),
			totalPnl: cumulativeTotalPnl,
			feesAndFunding: includeFeesAndFunding
				? cumulativeFeesAndFunding
				: undefined,
			isMockedSnapshot: !!snapshot.isMockedSnapshot,
		};
	});

	return {
		series,
		startOfPeriodTotalPnl,
		startOfPeriodFeesAndFunding,
	};
}

interface BuildCumulativePnlLiveDataParams extends BuildPortfolioChartLiveDataParams {
	startOfPeriodTotalPnl: number;
	startOfPeriodFeesAndFunding: number;
}

export function buildCumulativePnlLiveData({
	enabled,
	metrics,
	currentCumulativeFunding,
	currentUnrealizedPnl,
	includeFeesAndFunding,
	realizedPnlOnly,
	startOfPeriodTotalPnl,
	startOfPeriodFeesAndFunding,
}: BuildCumulativePnlLiveDataParams) {
	if (!enabled) {
		return {
			date: new Date(),
			totalPnl: 0,
			feesAndFunding: undefined,
			isMockedSnapshot: false,
		};
	}

	const { totalPnl: currentTotalPnl, feesAndFunding: currentFeesAndFunding } =
		computePortfolioPnlValues({
			cumulativeRealizedPnl: metrics?.cumulativeRealizedPnl,
			unrealizedPnl: currentUnrealizedPnl,
			cumulativeFunding: currentCumulativeFunding,
			cumulativeFeePaid: metrics?.cumulativeFeePaid,
			cumulativeFeeRebate: metrics?.cumulativeFeeRebate,
			includeFeesAndFunding,
			realizedPnlOnly,
		});

	const liveCumulativeTotalPnl = computePortfolioTradeDelta(
		currentTotalPnl,
		startOfPeriodTotalPnl
	);
	const liveCumulativeFeesAndFunding = computePortfolioTradeDelta(
		currentFeesAndFunding,
		startOfPeriodFeesAndFunding
	);

	return {
		date: new Date(),
		totalPnl: liveCumulativeTotalPnl,
		feesAndFunding: includeFeesAndFunding
			? liveCumulativeFeesAndFunding
			: undefined,
		isMockedSnapshot: false,
	};
}
